import { currentWorkspaceSlug } from "./workspace";

/**
 * What each screen last loaded on this visit, so coming back to a screen shows it at once while it
 * loads again quietly behind, the way the phone app keeps its screens loaded. Any change the person
 * makes forgets it all, so a screen never shows what was just changed; a sign-out or a switch of
 * business reloads the page, which forgets it too.
 */
const kept = new Map<string, unknown>();
const onTheWay = new Map<string, Promise<unknown>>();
/** Moves on with each change, so a load that set out before it is not kept after it. */
let era = 0;

const inWorkspace = (key: string) => `${currentWorkspaceSlug() ?? ""}|${key}`;

/** What `key` last loaded, if anything. */
export function lastLoaded<T>(key: string): T | null {
  return (kept.get(inWorkspace(key)) as T | undefined) ?? null;
}

/** Load `key` and keep what it brings; a load already on its way (one a finger started) is joined, not repeated. */
export function load<T>(key: string, fetch: () => Promise<T>): Promise<T> {
  const k = inWorkspace(key);
  const going = onTheWay.get(k) as Promise<T> | undefined;
  if (going) return going;
  const from = era;
  const loading = fetch()
    .then((value) => {
      if (from === era) kept.set(k, value);
      return value;
    })
    .finally(() => {
      if (onTheWay.get(k) === loading) onTheWay.delete(k);
    });
  onTheWay.set(k, loading);
  return loading;
}

/** Change every kept value whose key starts with `prefix`, so a screen's own change shows wherever it is kept. */
export function amend<T>(prefix: string, change: (value: T) => T): void {
  const p = inWorkspace(prefix);
  for (const [k, value] of kept) if (k.startsWith(p)) kept.set(k, change(value as T));
}

/** Start loading what the next screen needs as a finger lands on its row, so it opens ready. */
export function prefetch<T>(key: string, fetch: () => Promise<T>): void {
  load(key, fetch).catch(() => {});
}

/** Load `key` ahead of its screen, unless it is already kept or on its way. */
export function warm<T>(key: string, fetch: () => Promise<T>): void {
  const k = inWorkspace(key);
  if (!kept.has(k) && !onTheWay.has(k)) prefetch(key, fetch);
}

/** A change was made: forget everything kept and every load still on its way. */
export function forgetKept(): void {
  era += 1;
  kept.clear();
  onTheWay.clear();
}
